import { TICK_RATE, type Source } from './types';
import type { StarTest } from './levels';
import type { World } from './world';
import { scoreRun } from './scoring';

export interface RunMetrics {
  levelId: string;
  levelName: string;
  outcome: 'won' | 'lost' | 'playing';
  reason: string;
  seconds: number;
  goodReleases: number;
  verifiedReleases: number;
  flawedShipped: number;
  corruptShipped: number;
  peakRate: number;
  bestStreak: number;
  trustEnd: number;
  trustMin: number;
  creditsEnd: number;

  /** Seconds until the player's first build. */
  firstBuildSec: number;
  /** Longest stretch with no player action while the run was going. */
  longestIdleSec: number;
  actions: number;
  actionsBySource: Record<Source, number>;
  buildsTotal: number;
  buildsDelegated: number;
  /** Share of the factory built by drones, voice or the API. */
  delegationShare: number;
  /** Player actions run through voice, the API or drones. */
  delegatedActions: number;
  undos: number;

  explored: number;
  secretsFound: number;
  secretsTotal: number;
  claimed: number;

  asks: number;
  asksApproved: number;
  asksDenied: number;
  waitSeconds: number;
  acts: number;
  actsProtected: number;
  actsRisky: number;
  actsBackfired: number;

  incidents: number;
  incidentsContained: number;
  incidentsReachedUsers: number;
  maxBlast: number;
  avgBlast: number;
  /** Mean seconds from an incident to its resolution, for incidents that were resolved. */
  mttrSec: number;
  rollbacks: number;
  patches: number;
  breakerTrips: number;
  nestsDestroyed: number;
  glitchesKilled: number;
  research: number;
  firstResearchSec: number;

  // ---- First 20%: framing, architecture, taste
  /** Seconds from the start to the first good release: how fast you framed a working line. */
  firstGoodReleaseSec: number;
  machines: number;
  beltTiles: number;
  /** Peak releases a minute per machine. Higher means a simpler design for the result. */
  simplicity: number;
  lostUpdates: number;
  conflictsCaught: number;
  firstLostUpdateSec: number;
  /** Seconds after the first lost update that you placed a version gate, or -1. */
  gateAfterSec: number;
  /** Benches added between the first lost update and the first gate: treating a concurrency bug as a testing bug. */
  benchesInstead: number;
  perturbations: { id: string; kind: string; text: string; atSec: number; recoveredInSec: number | null }[];
  review: { read: boolean; predictions: number; predictionsTotal: number; bugsFound: number; bugsTotal: number; falseMarks: number; missed: string[] } | null;
  plan: { tests: number; rate: number; target: number; pieces: number; cost: number; overBudget: boolean } | null;
}

export interface RunReport {
  metrics: RunMetrics;
  stars: [boolean, boolean, boolean];
  agency: number;
  safety: number;
  archetype: { name: string; line: string };
  moments: string[];
  /** Evidence about the first 20%: how the player framed and re-framed problems. */
  framing: string[];
}

const PLAYER_SOURCES: Source[] = ['mouse', 'keys', 'command', 'voice', 'api'];
const DELEGATED: Source[] = ['voice', 'api', 'drone'];

export function clock(sec: number): string {
  const s = Math.max(0, Math.round(sec));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

export function computeMetrics(w: World): RunMetrics {
  const ev = w.events;
  const bySource = { mouse: 0, keys: 0, command: 0, voice: 0, api: 0, drone: 0, system: 0 } as Record<Source, number>;
  let firstBuild = -1;
  let builds = 0;
  let buildsDelegated = 0;
  let lastActionT = 0;
  let longestIdle = 0;
  for (const a of w.actionLog) {
    bySource[a.src]++;
    if (PLAYER_SOURCES.includes(a.src)) {
      longestIdle = Math.max(longestIdle, a.t - lastActionT);
      lastActionT = a.t;
    }
  }
  longestIdle = Math.max(longestIdle, w.tick - lastActionT);
  for (const e of ev) {
    if (e.type === 'place' && !e.ghost) {
      builds++;
      if (firstBuild < 0) firstBuild = e.t;
      if (DELEGATED.includes(e.src as Source)) buildsDelegated++;
    }
    if (e.type === 'droneBuild') {
      builds++;
      buildsDelegated++;
      bySource.drone++;
    }
    if (e.type === 'dronePatch') bySource.drone++;
  }
  const count = (type: string, pred?: (e: (typeof ev)[number]) => boolean) => ev.filter((e) => e.type === type && (!pred || pred(e))).length;
  const approvals = ev.filter((e) => e.type === 'approval');
  const acts = ev.filter((e) => e.type === 'act');
  const incidents = w.incidents;
  const spreading = incidents.filter((i) => i.cause !== 'flawed-release');
  const resolved = incidents.filter((i) => i.resolvedAt >= 0 && i.resolvedAt > i.t);
  const research = ev.filter((e) => e.type === 'research');
  const delegatedActions = bySource.voice + bySource.api + bySource.drone;
  return {
    levelId: w.level.id,
    levelName: w.level.name,
    outcome: w.outcome,
    reason: w.outcomeReason,
    seconds: w.seconds,
    goodReleases: w.goodReleases,
    verifiedReleases: w.verifiedReleases,
    flawedShipped: w.flawedShipped,
    corruptShipped: w.corruptShipped,
    peakRate: w.peakRate,
    bestStreak: w.bestStreak,
    trustEnd: Math.round(w.trust),
    trustMin: Math.round(w.trustMin),
    creditsEnd: Math.round(w.credits),
    firstBuildSec: firstBuild < 0 ? -1 : firstBuild / TICK_RATE,
    longestIdleSec: longestIdle / TICK_RATE,
    actions: w.actionLog.length,
    actionsBySource: bySource,
    buildsTotal: builds,
    buildsDelegated,
    delegationShare: builds ? buildsDelegated / builds : 0,
    delegatedActions,
    undos: count('undo'),
    explored: w.exploredPct(),
    secretsFound: w.secrets.filter((s) => s.found).length,
    secretsTotal: w.secrets.length,
    claimed: count('claim'),
    asks: count('ask'),
    asksApproved: approvals.filter((e) => e.approved).length,
    asksDenied: approvals.filter((e) => !e.approved).length,
    waitSeconds: approvals.reduce((s, e) => s + Number(e.waited ?? 0), 0),
    acts: acts.length,
    actsProtected: acts.filter((e) => e.protected).length,
    actsRisky: acts.filter((e) => e.risky).length,
    actsBackfired: acts.filter((e) => e.backfired).length,
    incidents: incidents.length,
    incidentsContained: incidents.filter((i) => i.contained).length,
    incidentsReachedUsers: incidents.filter((i) => i.reachedUsers).length,
    maxBlast: spreading.reduce((m, i) => Math.max(m, i.affected), 0),
    avgBlast: spreading.length ? spreading.reduce((s, i) => s + i.affected, 0) / spreading.length : 0,
    mttrSec: resolved.length ? resolved.reduce((s, i) => s + (i.resolvedAt - i.t), 0) / resolved.length / TICK_RATE : 0,
    rollbacks: count('rollback'),
    patches: count('patch') + count('dronePatch'),
    breakerTrips: count('trip'),
    nestsDestroyed: count('nestDestroyed'),
    glitchesKilled: count('glitchKilled'),
    research: research.length,
    firstResearchSec: research.length ? research[0].t / TICK_RATE : -1,
    ...framingMetrics(w),
  };
}

function framingMetrics(w: World) {
  const ev = w.events;
  const sec = (t: number) => t / TICK_RATE;
  const firstGood = w.releaseTicks.length ? w.releaseTicks[0] : -1;
  const placeable = [...w.buildings.values()].filter((b) => !b.ghost && b.kind !== 'users' && b.kind !== 'nest' && b.kind !== 'monolith' && b.kind !== 'terminal' && b.kind !== 'cache');
  const beltTiles = placeable.filter((b) => b.kind === 'belt' || b.kind === 'breaker' || b.kind === 'gate').length;
  const machines = placeable.length - beltTiles;
  const lost = w.incidents.filter((i) => i.cause === 'lost-update');
  const firstLost = lost.length ? lost[0].t : -1;
  let gateAfter = -1;
  let benchesInstead = 0;
  if (firstLost >= 0) {
    const gates = ev.filter((e) => (e.type === 'place' || e.type === 'droneBuild') && e.kind === 'gate' && e.t >= firstLost && !e.ghost);
    const firstGate = gates.length ? gates[0].t : Infinity;
    if (gates.length) gateAfter = sec(firstGate - firstLost);
    benchesInstead = ev.filter((e) => (e.type === 'place' || e.type === 'droneBuild') && e.kind === 'bench' && e.t >= firstLost && e.t < firstGate && !e.ghost).length;
  }
  const perturbations = w.perturbs.map((p) => ({
    id: p.def.id, kind: p.def.kind, text: p.def.text, atSec: sec(p.firedAt), recoveredInSec: p.recoveredAt >= 0 ? sec(p.recoveredAt - p.firedAt) : null,
  }));
  let review: RunMetrics['review'] = null;
  const rd = w.level.review;
  if (rd) {
    const tagOf = new Map<number, string>();
    for (const [tag, id] of w.tags) tagOf.set(id, tag);
    const markedTags = [...w.marks].map((id) => tagOf.get(id) ?? `#${id}`);
    const found = rd.bugs.filter((b) => b.hot.some((h) => markedTags.includes(h)));
    const falseMarks = markedTags.filter((t) => !rd.bugs.some((b) => b.hot.includes(t))).length;
    review = {
      read: w.answers.get('read') === String(rd.read.answer),
      predictions: rd.predict.filter((q) => w.answers.get(q.id) === q.answer).length,
      predictionsTotal: rd.predict.length,
      bugsFound: found.length,
      bugsTotal: rd.bugs.length,
      falseMarks,
      missed: rd.bugs.filter((b) => !found.includes(b)).map((b) => b.name),
    };
  }
  let plan: RunMetrics['plan'] = null;
  if (w.level.flags.planOnly) {
    const r = w.planResult;
    plan = {
      tests: ev.filter((e) => e.type === 'planTest').length,
      rate: r?.rate ?? 0, target: w.level.planTarget ?? 0, pieces: r?.pieces ?? 0, cost: r?.cost ?? 0, overBudget: !!r?.overBudget,
    };
  }
  return {
    firstGoodReleaseSec: firstGood < 0 ? -1 : sec(firstGood),
    machines,
    beltTiles,
    simplicity: machines ? Math.round((w.peakRate / machines) * 10) / 10 : 0,
    lostUpdates: w.lostUpdates,
    conflictsCaught: w.conflictsCaught,
    firstLostUpdateSec: firstLost < 0 ? -1 : sec(firstLost),
    gateAfterSec: gateAfter,
    benchesInstead,
    perturbations,
    review,
    plan,
  };
}

export function testStar(t: StarTest, m: RunMetrics, w: World): boolean {
  switch (t.type) {
    case 'time':
      return m.seconds <= t.max;
    case 'secret':
      return w.secrets.some((s) => s.id === t.id && s.found);
    case 'event':
      return w.events.some((e) => e.type === t.name);
    case 'trust':
      return m.trustEnd >= t.min;
    case 'noCorruptRelease':
      return m.corruptShipped === 0;
    case 'maxFlawed':
      return m.flawedShipped <= t.max;
    case 'nests':
      return m.nestsDestroyed >= t.min;
    case 'research':
      return m.research >= t.min;
    case 'protectedActs':
      return m.acts >= t.min && m.actsProtected === m.acts;
    case 'noAsks':
      return m.asks === 0;
    case 'monolith':
      return w.monolithTapped;
    case 'delegation':
      return m.delegationShare >= t.min;
    case 'delegated':
      return m.delegatedActions >= t.min;
    case 'noUncontainedLate': {
      // Shipped hallucinations are a rate, not an incident to contain: about one
      // in twelve bad drafts gets past a bench. Count only incidents you could stop.
      const from = w.tick - t.window * TICK_RATE;
      return !w.incidents.some((i) => i.t >= from && !i.contained && i.cause !== 'flawed-release');
    }
    case 'recovered':
      return m.perturbations.filter((p) => p.recoveredInSec !== null).length >= t.min;
    case 'noLostUpdates':
      return m.lostUpdates === 0;
    case 'review':
      if (!m.review) return false;
      if (t.what === 'read') return m.review.read;
      if (t.what === 'predict') return m.review.predictions === m.review.predictionsTotal;
      return m.review.bugsFound === m.review.bugsTotal && m.review.falseMarks <= 1;
    case 'plan':
      return w.level.flags.planOnly ? w.outcome === 'won' : false;
    case 'and':
      return t.tests.every((x) => testStar(x, m, w));
  }
}

function archetype(agency: number, safety: number): { name: string; line: string } {
  if (agency >= 50 && safety >= 50) return { name: 'AI-native', line: 'You moved without waiting and kept what broke small.' };
  if (agency >= 50) return { name: 'Cowboy', line: 'Bold and fast. The blast radius followed you around.' };
  if (safety >= 50) return { name: 'Careful', line: 'Nothing big broke, but you waited for things you could have done.' };
  return { name: 'Passenger', line: 'The factory happened to you more than you happened to it.' };
}

function moments(m: RunMetrics, w: World): string[] {
  const out: string[] = [];
  if (m.firstBuildSec >= 0) out.push(`First build at ${clock(m.firstBuildSec)}.`);
  for (const s of w.secrets.filter((x) => x.found)) out.push(`Found ${s.name.toLowerCase()} at ${clock(s.foundAt / TICK_RATE)}.`);
  if (m.asks) {
    out.push(`Asked for permission ${m.asks} time${m.asks === 1 ? '' : 's'} and waited ${clock(m.waitSeconds)} in total${m.asksDenied ? `; ${m.asksDenied} denied` : ''}.`);
  }
  if (m.acts) {
    out.push(`Acted without asking ${m.acts} time${m.acts === 1 ? '' : 's'}: ${m.actsProtected} with a checkpoint behind you${m.actsBackfired ? `, ${m.actsBackfired} blew up` : ''}.`);
  }
  if (m.maxBlast > 1) {
    const worst = w.incidents.filter((i) => i.cause !== 'flawed-release').sort((a, b) => b.affected - a.affected)[0];
    if (worst) out.push(`Worst incident: ${worst.origin} at ${clock(worst.t / TICK_RATE)} corrupted ${worst.affected} machines.`);
  }
  if (m.breakerTrips) out.push(`Breakers stopped corrupted work ${m.breakerTrips} time${m.breakerTrips === 1 ? '' : 's'}.`);
  if (m.rollbacks) out.push(`Rolled back ${m.rollbacks} time${m.rollbacks === 1 ? '' : 's'}.`);
  if (m.flawedShipped) out.push(`${m.flawedShipped} hallucinated release${m.flawedShipped === 1 ? '' : 's'} reached users.`);
  if (m.nestsDestroyed) out.push(`Cleared ${m.nestsDestroyed} nest${m.nestsDestroyed === 1 ? '' : 's'}.`);
  if (m.actionsBySource.voice) out.push(`Your voice copilot ran ${m.actionsBySource.voice} commands.`);
  if (m.actionsBySource.api) out.push(`Scripts drove ${m.actionsBySource.api} actions through the API.`);
  if (m.buildsDelegated) out.push(`${m.buildsDelegated} of ${m.buildsTotal} things were built by something other than your hands.`);
  if (m.longestIdleSec > 45) out.push(`Longest stretch without doing anything: ${clock(m.longestIdleSec)}.`);
  return out;
}

const PERTURB_NAME: Record<string, string> = {
  concurrency: 'two teams started editing the same records',
  computeCut: 'compute was cut',
  surge: 'requests doubled',
  modelUpdate: 'the model got worse',
  rackFailure: 'a rack died',
};

/** Plain evidence about the first 20%, for a reviewer deciding yes or no. */
function framing(m: RunMetrics, w: World): string[] {
  const out: string[] = [];
  // In the review you read someone else's factory; you can't build, so build signals don't apply.
  const building = !m.review && !m.plan;
  // When the factory came prebuilt, its first release isn't the player's work.
  const prebuilt = w.level.features.some((f) => f.t === 'build');
  if (building && !prebuilt && m.firstGoodReleaseSec >= 0) out.push(`First working line shipped at ${clock(m.firstGoodReleaseSec)}.`);
  if (building && prebuilt && w.level.goal.type === 'rate' && w.outcome !== 'won') out.push(`Never got the inherited line to ${w.level.goal.perMin} a minute (peaked at ${m.peakRate}).`);
  for (const p of m.perturbations) {
    if (!p.text) continue;
    out.push(p.recoveredInSec === null ? `At ${clock(p.atSec)} ${PERTURB_NAME[p.kind] ?? p.kind}. The factory never got back to its old pace.` : `At ${clock(p.atSec)} ${PERTURB_NAME[p.kind] ?? p.kind}. Back to pace in ${clock(p.recoveredInSec)}.`);
  }
  if (building && m.firstLostUpdateSec >= 0) {
    if (m.gateAfterSec >= 0) out.push(`Lost updates started at ${clock(m.firstLostUpdateSec)}. You put a version gate in ${clock(m.gateAfterSec)} later${m.benchesInstead ? `, after first adding ${m.benchesInstead} bench${m.benchesInstead === 1 ? '' : 'es'}` : ''}.`);
    else out.push(`Lost updates started at ${clock(m.firstLostUpdateSec)} and never got a structural fix${m.benchesInstead ? `. You added ${m.benchesInstead} bench${m.benchesInstead === 1 ? '' : 'es'}, but tests can't see a race` : ''}.`);
    if (m.conflictsCaught) out.push(`Version gates stopped ${m.conflictsCaught} stale change${m.conflictsCaught === 1 ? '' : 's'}.`);
  }
  if (m.review) {
    const r = m.review;
    out.push(`Read the inherited factory ${r.read ? 'correctly' : 'wrong'}. Predicted ${r.predictions} of ${r.predictionsTotal} failures. Found ${r.bugsFound} of ${r.bugsTotal} bugs${r.falseMarks ? ` with ${r.falseMarks} false alarm${r.falseMarks === 1 ? '' : 's'}` : ''}${r.missed.length ? `. Missed: ${r.missed.join('; ')}` : ''}.`);
  }
  if (m.plan) {
    const p = m.plan;
    out.push(`Planned ${p.pieces} pieces and tested the plan ${p.tests} time${p.tests === 1 ? '' : 's'}. In simulation it ships ${p.rate} a minute against a target of ${p.target}${p.overBudget ? `, but it's over budget at ${p.cost}` : ''}.`);
  }
  if (building && m.machines && m.peakRate) out.push(`Peaked at ${m.peakRate} a minute with ${m.machines} machines and ${m.beltTiles} belt tiles (${m.simplicity} per machine).`);
  return out;
}

export function buildReport(w: World): RunReport {
  const m = computeMetrics(w);
  const won = m.outcome === 'won';
  const [s2, s3] = w.level.stars;
  const stars: [boolean, boolean, boolean] = [won, won && testStar(s2.test, m, w), won && testStar(s3.test, m, w)];
  const { agency, safety } = scoreRun(m);
  const a = Math.max(0, Math.min(100, Math.round(agency)));
  const s = Math.max(0, Math.min(100, Math.round(safety)));
  return { metrics: m, stars, agency: a, safety: s, archetype: archetype(a, s), moments: moments(m, w), framing: framing(m, w) };
}

/** Everything needed to replay or audit a run. */
export function exportRun(w: World, report: RunReport) {
  return {
    format: 'blast-radius-run/1',
    exportedFor: 'replay and review',
    level: { id: w.level.id, name: w.level.name, seed: w.level.seed },
    ticks: w.tick,
    tickRate: TICK_RATE,
    actions: w.actionLog,
    events: w.events,
    snapshots: w.snapshots,
    incidents: w.incidents,
    report,
  };
}
